/**
 * Reading the `/cli-login` search params.
 *
 * ## The router parses them, so these are not all strings
 *
 * TanStack Router runs the query string through JSON-ish parsing before
 * `validateSearch` sees it, so `?port=51862` arrives as the **number** 51862.
 * That is the whole reason these helpers exist as a tested pair rather than an
 * inline `String()`.
 *
 * The failure it caused is worth recording, because nothing about it looked
 * like a type problem: `asPort` accepted only strings, so every real port
 * became `""`, while `code` and `label` — which never look numeric — arrived
 * intact. The page then reported it had "a code and no port", the CLI waited on
 * a callback that could never come, and the URL in the address bar looked
 * perfectly correct the whole time.
 */

/**
 * A search param as a string, or "" for anything that is not one.
 *
 * `String()` would turn a repeated param — which arrives as an array — into
 * "[object Object]" and send that to the server as a login code. Empty is the
 * honest answer, and the page already handles it.
 */
export function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * A TCP port as a string, or "".
 *
 * Accepts a number as well as a string, for the parsing reason above.
 *
 * The validation is not cosmetic. This value is interpolated into the redirect
 * that carries the login code back to the CLI, so an unvalidated one is an open
 * redirect that leaks a credential: `@evil.com` makes
 * `http://127.0.0.1:@evil.com/callback?code=…`, where everything before the `@`
 * is userinfo and the real host is `evil.com`. Digits only, and in range —
 * anything else is refused rather than repaired, because a port is a number or
 * it is an attack.
 */
export function asPort(value: unknown): string {
  const raw =
    typeof value === "number" && Number.isInteger(value)
      ? String(value)
      : asString(value);

  if (!/^[0-9]{1,5}$/.test(raw)) return "";

  const port = Number(raw);
  return port >= 1 && port <= 65535 ? raw : "";
}

/**
 * Where the approved code goes back to: the CLI's loopback listener, or null
 * if the URL would point anywhere else.
 *
 * Built through `URL` and re-checked rather than trusting `asPort` alone. The
 * validator is the guard; this is the assertion that the thing about to
 * receive a login code really is loopback, at the one place where getting it
 * wrong sends the code to someone else.
 */
export function loopbackCallbackUrl(port: string, code: string): string | null {
  let target: URL;
  try {
    target = new URL(`http://127.0.0.1:${port}/callback`);
  } catch {
    return null;
  }
  if (target.hostname !== "127.0.0.1") return null;

  target.searchParams.set("code", code);
  return target.toString();
}
